// First-run business setup (UI kit 12): name, logo and contact first; then
// address, bank details and tax. Either step can be skipped, and whatever
// was entered is kept. Everything can be changed later in Settings.
import { type FormEvent, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppData, useUid } from '@/app/AppData';
import { usePageTitle } from '@/app/usePageTitle';
import { type Business, canAddBusiness, newBusiness } from '@/core/business';
import { isEmailAddress } from '@/core/client';
import { nowStamp } from '@/core/dates';
import { newId } from '@/core/ids';
import { NIGERIA_VAT, NO_TAX } from '@/core/invoice';
import { CURRENCIES, formatBasisPoints, parseBasisPoints } from '@/core/money';
import { track } from '@/data/analytics';
import { saveBusiness } from '@/data/store';
import { currencyLabel } from '@/l10n/labels';
import { t } from '@/l10n/strings';
import { Button, LogoMark, SelectField, Switch, TextArea, TextField } from '@/ui/controls';
import { LogoPicker } from '@/ui/LogoPicker';

export function SetupPage() {
  const uid = useUid();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Adding another business: it can be cancelled, and isn't skipped.
  const adding = params.get('add') === '1';
  const { setActiveBusiness, businesses, isPro } = useAppData();
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [logo, setLogo] = useState<string | null>(null);
  const [currency, setCurrency] = useState('NGN');
  const [address, setAddress] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [chargesTax, setChargesTax] = useState(true);
  const [taxName, setTaxName] = useState(NIGERIA_VAT.name);
  const [taxRate, setTaxRate] = useState(formatBasisPoints(NIGERIA_VAT.basisPoints).replace('%', ''));
  const [showErrors, setShowErrors] = useState(false);
  usePageTitle(t('setupTellUsAboutYourBusiness'));

  const nameError = !name.trim() ? t('errorBusinessNameMissing') : null;
  const emailError = email.trim() && !isEmailAddress(email) ? t('errorEmailInvalid') : null;
  const rate = parseBasisPoints(taxRate);
  const taxError = chargesTax && (rate === null || rate <= 0) ? t('errorTaxRateInvalid') : null;

  const changeCurrency = (code: string) => {
    setCurrency(code);
    // Nigeria's VAT is the default for naira; elsewhere the rate depends on the country.
    const naira = code === 'NGN';
    setChargesTax(naira);
    if (naira) {
      setTaxName(NIGERIA_VAT.name);
      setTaxRate(formatBasisPoints(NIGERIA_VAT.basisPoints).replace('%', ''));
    }
  };

  const save = (skipped: boolean) => {
    const now = nowStamp();
    const business: Business = {
      ...newBusiness({ id: newId(), name, currency, now }),
      email: email.trim(),
      phone: phone.trim(),
      address: address.trim(),
      logo,
      bank: { bankName: bankName.trim(), accountName: accountName.trim(), accountNumber: accountNumber.trim() },
      tax: chargesTax && rate !== null && rate > 0 ? { name: taxName.trim() || t('taxDefaultName'), basisPoints: rate } : NO_TAX,
      defaultNotes: t('businessDefaultNotes'),
    };
    saveBusiness(uid, business);
    // The new business is the one in use from now on.
    setActiveBusiness(business.id);
    track('business_setup_finished', {
      skipped,
      currency,
      has_logo: logo !== null,
      has_bank_details: !!(bankName.trim() || accountNumber.trim()),
      charges_tax: business.tax.basisPoints > 0,
    });
    navigate(params.get('then') === 'invoice' ? '/invoices/new' : '/home', { replace: true });
  };

  const next = (e: FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (step === 1) {
      if (nameError || emailError) return;
      setShowErrors(false);
      setStep(2);
    } else {
      if (taxError) return;
      save(false);
    }
  };

  const skip = () => {
    if (step === 1 && nameError) {
      setShowErrors(true);
      return;
    }
    save(true);
  };

  // Typed in past the limit: Settings explains it.
  if (adding && businesses && !canAddBusiness(businesses.length, isPro)) return <Navigate to="/settings/businesses" replace />;
  return (
    <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '48px 16px' }}>
      <form className="card stack-6" style={{ width: 'min(620px, 100%)', padding: 32 }} onSubmit={next} noValidate>
        <div className="between">
          <LogoMark size={40} />
          <span className="caption">{t('setupStep', { step })}</span>
        </div>
        <div className="row" style={{ gap: 6 }} aria-hidden>
          <span style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--brand)' }} />
          <span style={{ flex: 1, height: 4, borderRadius: 2, background: step === 2 ? 'var(--brand)' : 'var(--surface-2)' }} />
        </div>
        {step === 1 ? (
          <>
            <div className="stack-2">
              <h1 className="display">{adding ? t('businessesAddingTitle') : t('setupTellUsAboutYourBusiness')}</h1>
              <p className="body muted">{t('setupThisGoesAtTheTop')}</p>
            </div>
            <LogoPicker logo={logo} name={name} onChange={setLogo} />
            <TextField label={t('setupBusinessName')} value={name} onChange={(e) => setName(e.target.value)} error={showErrors ? nameError : null} autoFocus autoComplete="organization" />
            <div className="grid-2">
              <TextField label={t('setupBusinessEmail')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={showErrors ? emailError : null} autoComplete="email" />
              <TextField label={t('setupPhone')} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
            </div>
            <SelectField label={t('setupCurrency')} value={currency} onChange={(e) => changeCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} {c.symbol} · {currencyLabel(c.code)}
                </option>
              ))}
            </SelectField>
          </>
        ) : (
          <>
            <div className="stack-2">
              <h1 className="display">{t('setupHowClientsPayYou')}</h1>
              <p className="body muted">{t('setupYourAddressAndBankDetails')}</p>
            </div>
            <TextArea label={t('setupBusinessAddress')} value={address} onChange={(e) => setAddress(e.target.value)} rows={2} autoComplete="street-address" autoFocus />
            <div className="grid-2">
              <TextField label={t('setupBankName')} value={bankName} onChange={(e) => setBankName(e.target.value)} />
              <TextField label={t('setupAccountNumber')} value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} inputMode="numeric" />
            </div>
            <TextField label={t('setupAccountName')} value={accountName} onChange={(e) => setAccountName(e.target.value)} />
            <div>
              <Switch label={t('setupAddTaxToInvoices')} detail={t('setupYouCanTurnItOff')} checked={chargesTax} onChange={setChargesTax} />
              {chargesTax && (
                <div className="grid-2" style={{ marginTop: 8 }}>
                  <TextField label={t('setupTaxName')} value={taxName} onChange={(e) => setTaxName(e.target.value)} />
                  <TextField label={t('setupRate')} value={taxRate} onChange={(e) => setTaxRate(e.target.value)} inputMode="decimal" error={showErrors ? taxError : null} />
                </div>
              )}
            </div>
          </>
        )}
        <div className="between">
          {step === 2 ? (
            <Button variant="ghost" onClick={() => setStep(1)} icon="back">
              {t('commonBack')}
            </Button>
          ) : adding ? (
            <Button variant="ghost" onClick={() => navigate(-1)}>
              {t('commonCancel')}
            </Button>
          ) : (
            <span />
          )}
          <div className="row">
            {!adding && (
              <Button variant="ghost" onClick={skip}>
                {t('commonSkip')}
              </Button>
            )}
            <Button type="submit" variant="action">
              {step === 1 ? t('commonContinue') : t('setupFinish')}
            </Button>
          </div>
        </div>
      </form>
    </main>
  );
}
